<script setup lang="ts">
import { reactive } from 'vue'
import ReviewItem from '~/components/objects/Reviews/ReviewItem.vue'
import ReviewStars from '~/components/objects/Reviews/ReviewStars.vue'
import CommentAggregate from '~/models/CommentAggregate/CommentAggregate'

const props = defineProps({
    feedId: {
        type: Number,
        required: true,
    },
    feedName: {
        type: String,
        required: true,
    },
    feedDescription: {
        type: String,
        required: true,
    },
    feedType: {
        type: String,
        required: true,
    },
    reviews: {
        type: Array as PropType<CommentAggregate[]>,
        required: true,
    },
    totalReview: {
        type: Number,
        default: 0,
    },
    dataLoaded: {
        type: Boolean,
        default: false,
    },
    recommendPercentage: {
        type: Number,
        default: 0,
    },
})

const { feedName, feedDescription, totalReview, reviews, recommendPercentage } = toRefs(props)

const state = reactive({
    title: feedName.value,
    description: feedDescription.value,
    rating: totalReview.value,
    reviews: reviews.value,
    recommendPercentage: recommendPercentage.value,
})

const emit = defineEmits(['reviewsCount'])

onMounted(async () => {
    if (props.dataLoaded) {
        return
    }

    try {
        const { data } = await useCachedAsyncData(`comment_aggregate_${props.feedId}_${props.feedType}`, 600, async () => {
            return await useNuxtApp().$repository.getCommentAggregate(props.feedId, props.feedType)
        })
        state.rating = data.value.rating
        state.reviews = data.value.reviews
        state.recommendPercentage = data.value.recommendPercentage

        emit('reviewsCount', state.reviews.length)
    } catch (error) {
        emit('reviewsCount', 0)
    }
})
</script>

<template>
    <div id="review_feed" class="review_feed__wrapper">
        <div class="review_feed__title">{{ $t('reviews.title', { feedName: state.title }) }}</div>
        <div class="review_feed__description">{{ $t('reviews.description', { feedName: state.title }) }}</div>
        <div v-if="state.rating > 0" class="review_feed__rating">
            <div class="rating_text">
                {{ $t('reviews.rating', { feedName: feedName, rating: state.recommendPercentage }) }}
            </div>
            <ReviewStars :rating="state.rating * 20"></ReviewStars>
        </div>

        <div class="review_feed__items">
            <ReviewItem v-for="review in state.reviews" :key="review.id" :review="review" />
        </div>
    </div>
</template>

<style scoped lang="scss">
.review_feed {
    &__title {
        font-size: 24px;
        font-weight: 590;
        margin-bottom: 1rem;
    }

    &__description {
        color: $color-dark;
        font-size: 14px;
        font-weight: 400;
        margin-bottom: 1rem;
    }

    &__rating {
        display: inline-flex;
        flex-wrap: nowrap;
        align-items: center;
        background-color: $color-yellow-light;
        font-size: 12px;
        padding: 0.25rem 1rem;
        border-radius: 50px;
        gap: 0.5rem;
        margin-bottom: 1rem;

        .rating_text {
            display: inline-block;
        }
    }

    &__items {
        display: grid;
        grid-template-columns: 1fr;
        gap: 1rem;

        @include screen-lg {
            grid-template-columns: repeat(2, 1fr);
        }
    }
}
</style>
